<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'HeadComponent',
})
</script>
<template>
  <div class="header-container">
    <!-- 头部导航栏 -->
    <div class="custom-head">
      <!-- 左侧内容插槽（默认显示平台标题） -->
      <div class="left-content">
        <slot name="left">
          <a-typography-title :level="4" style="color: white; margin: 0">
            &nbsp;
            <PieChartFilled style="padding-left: 10px; color: white" />
            &nbsp;全面质量管理平台
          </a-typography-title>
        </slot>
      </div>

      <!-- 右侧内容插槽（用户信息、下拉菜单等） -->
      <div class="right-content">
        <slot name="right">
          <span class="menu-container">
            <a-space :size="12">
              <!-- 用户信息图标 -->
              <UserOutlined @click="infomsg" style="font-size: 18px; cursor: pointer" />

              <!-- 用户信息弹窗 -->
              <UserInfoModal :visible="userDialogVisible" @update:visible="handleUserModalVisible" @cancel="closeUserModal" :user-info="currentUserInfo" />

              <!-- 密码修改弹窗 -->
              <PasswordResetModal :visible="passwordDialogVisible" :username="props.user?.username || ''" @update:visible="passwordDialogVisible = $event" @success="handlePasswordResetSuccess" />

              <!-- 用户名显示 -->
              <a style="font-size: 14px; color: white; white-space: nowrap; cursor: pointer" @click="infomsg">
                {{ props.user?.name || '未知用户' }}
              </a>

              <!-- 用户下拉菜单 -->
              <a-dropdown v-model:open="dropdownVisible" placement="bottomRight" :overlayStyle="{ marginTop: '128px', overflow: 'visible' }" class="custom-dropdown">
                <a class="ant-dropdown-link" style="color: white; padding-right: 20px; white-space: nowrap" @click.prevent>
                  <EllipsisOutlined style="font-size: 16px" />
                </a>
                <template #overlay>
                  <a-menu @click="handleMenuClick">
                    <a-menu-item key="1">用户信息</a-menu-item>
                    <a-menu-item key="2">修改密码</a-menu-item>
                    <a-menu-item key="3">退出登录</a-menu-item>
                  </a-menu>
                </template>
              </a-dropdown>
            </a-space>
          </span>
        </slot>
      </div>
    </div>

    <!-- Dm组件：数据请求工具 -->
    <div>
      <DM  ref="dm" />
    </div>

    <!-- 应用列表区域（删除内联样式，统一在style中维护） -->
    <div class="content">
      <div class="appContainer">
        <!-- 应用列表 -->
        <ul class="appUl" :class="{ 'single-item': filteredItems.length === 1 }">
          <li v-for="(value, index) in tableData" :key="value.MENUCODE || index" class="auto-track" :data-log-value="value.SysName || ''">
            <!-- 应用项组件 -->
            <AppItem
              :icon-key="value.Icon?.toLowerCase() || 'default'"
              :menu-name="value.MENUNAME || '未知应用'"
              :sys-name="value.SysName || '未知应用'"
              :path="value.Path || ''"
              :menu-code="value.MENUCODE || ''"
              :parent-code="value.PARENTCODE || ''"
              @item-click="clickitem"
            />
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup name="HeadComponent">
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import router from '@/router'
import { message, Modal } from 'ant-design-vue'
import DM  from '/src/utils/dm.vue'
import f, { sysInfo } from '/src/utils/f.ts'
import UserInfoModal from '/src/views/firstpage/components/UserInfoModal.vue'
import PasswordResetModal from '/src/views/firstpage/components/PasswordResetModal.vue'
// 仅保留实际使用的图标
import { UserOutlined, EllipsisOutlined, PieChartFilled } from '@ant-design/icons-vue'

// 工具函数导入
import { clearUserInfo } from '@/utils/util'
import { logout } from '@/views/user/service'
import ls from '/src/utils/storage'
import { ACCESS_TOKEN } from '/src/store/mutation-types'
import AppItem from '/src/views/firstpage/components/AppItem.vue'

// 响应式变量：仅保留必要项
const passwordDialogVisible = ref(false)
const tableData = ref([])
const userDialogVisible = ref(false)
const currentUserInfo = ref({
  empid: '',
  name: '',
  hospital: '',
  deptName: ''
})
const dm = ref(null)
const dropdownVisible = ref(false) // 用于 a-dropdown 的 open 绑定
const formState = ref({
  username: '' // 仅用于传递给 PasswordResetModal
})

// 计算属性：保留过滤逻辑
const filteredItems = computed(() => {
  if (!tableData.value) return []
  return Object.values(tableData.value).filter((item) => item?.MENUNAME === '系统管理')
})

// 类型定义：仅保留必要字段
interface User {
  username: string
  name: string
  unitname?: string
  deptname?: string
}

// Props：仅保留 user 字段
const props = defineProps({
  user: {
    type: Object as () => User,
    required: true
  }
})

// 国际化
const { t } = useI18n()

// 方法：仅保留必要逻辑
const showPasswordModal = () => {
  passwordDialogVisible.value = true
}

const handlePasswordResetSuccess = () => {
  console.log('密码修改成功')
  message.success('密码修改成功') // 可补充反馈，原代码无提示
}

const showUserInfo = () => {
  fetchUserInfo()
  userDialogVisible.value = true
}

const fetchUserInfo = () => {
  try {
    currentUserInfo.value = {
      empid: props.user?.username,
      name: props.user?.name || '',
      hospital: props.user?.unitname || '',
      deptName: props.user?.deptname || ''
    }
  } catch (error) {
    console.error('获取用户信息失败:', error)
    message.error('获取用户信息失败')
  }
}

const isHttpOrHttpsURL = (url: string) => {
  return url.startsWith('http://') || url.startsWith('https://')
}

const clickitem = async (SysUrl: string, SysName: string, PARENTCODE: string, Path: string) => {
  if (SysUrl === '') {
    f.error(`未配置系统链接`)
    return
  }

  const token = ls.get(ACCESS_TOKEN)
  localStorage.setItem('token', token)
  Object.keys(sysInfo).forEach((key) => {
    localStorage.setItem(key, sysInfo[key])
  })

  const data = {
    token,
    userId: sysInfo.UserID,
    userName: sysInfo.UserName,
    BaseGroupGUID: sysInfo.BaseGroupGUID,
    DeptName: sysInfo.DeptName,
    GroupName: sysInfo.GroupName,
    UnitName: sysInfo.UnitName,
    UserGUID: sysInfo.UserGUID,
    Sex: sysInfo.Sex,
    BaseUrl: sysInfo.BaseUrl,
    HomeUrl: sysInfo.HomeUrl,
    CurrModuleID: PARENTCODE,
    CurrModule: SysName,
    CurrModulePath: Path
  }

  window.name = JSON.stringify(data)

  if (!isHttpOrHttpsURL(SysUrl)) {
    sysInfo.CurrModuleID = PARENTCODE
    sysInfo.CurrModule = SysName
    sysInfo.CurrModulePath = Path
    sysInfo.DefaultPageUrl = ''
    sysInfo.CurrItem = ''
    router.push(SysUrl)
  } else {
    window.location.href = SysUrl
  }
}

const handleMenuClick = (e: any) => {
  // 若有类型定义，可替换为 `MenuProps['onClick']`（需导入 MenuProps）
  if (e.key === '3') {
    dropdownVisible.value = false
    Modal.confirm({
      title: t('tools.userMenu.tip'),
      content: t('tools.userMenu.checkLogout'),
      onOk: () => {
        logout().then(() => {
          clearUserInfo()
          router.push({ path: '/user/login' })
        })
      }
    })
  } else if (e.key === '2') {
    dropdownVisible.value = false
    showPasswordModal()
  } else if (e.key === '1') {
    showUserInfo()
    dropdownVisible.value = false
  }
}

const infomsg = () => {
  showUserInfo()
}

// 2. 处理用户信息弹窗的visible更新
const handleUserModalVisible = (val: boolean) => {
  console.log('用户信息弹窗状态变更:', val)
  userDialogVisible.value = val
}

// 3. 关闭用户信息弹窗
const closeUserModal = () => {
  console.log('关闭用户信息弹窗')
  userDialogVisible.value = false
}

// 生命周期：保留数据请求逻辑
onMounted(async () => {
  if (props.user?.username) {
    formState.value.username = props.user.username
  }

  try {
    const params = {
      apicode: 'SQLScript_2503090001',
      usercode: formState.value.username
    }
    const data = await dm.value.data(`/api/comm/data/`, params)
    if (data?.data?.list) {
      tableData.value = data.data.list
    }
  } catch (error) {
    console.error('获取应用列表失败:', error) // 修正错误提示（原提示“登录失败”不准确）
    message.error('获取应用列表失败，请检查网络')
  }
})

// Emits：保留必要事件（若父组件使用）
const emit = defineEmits({
  'default-action-click': () => true
})
</script>

<style scoped lang="less">
/* 根元素统一 box-sizing */
.header-container {
  width: 100%;
  box-sizing: border-box;
  * {
    box-sizing: inherit;
  }
}

/* 头部导航栏 */
.custom-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #0056b9;
  width: 100%;
  height: 100%;
  border-bottom: 2px solid #6bafff;
  border-top: 1px solid #0051ad;
  padding: 0;
}

/* 头部左侧 */
.left-content {
  color: white;
  padding-left: 16px;
}

/* 头部右侧 */
.right-content {
  font-size: 14px;
  display: flex;
  gap: 12px;
  align-items: center;
  color: white;
  padding: 0 12px;
  white-space: nowrap;
  flex-shrink: 0;
}

/* 应用列表外层容器 */
.content {
  display: flex;
  align-items: center;
  flex-direction: column;
  height: 90vh;
  padding: 20px;
  background-color: #fff;
}

/* 应用列表容器 */
.appContainer {
  margin-top: 100px;
  width: 100%;
  max-width: 1176px;
  margin: 100px auto 72px; /* 合并 margin */
  text-align: center;
  cursor: pointer;
}

/* 应用列表 UL */
.appUl {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 20px;
  margin: 0;
  padding: 0;
  list-style: none;
}

/* 应用列表 LI */
.appUl li {
  width: 210px;
  margin-bottom: 20px;
  padding: 0;
}

/* 单个应用项居中 */
.appUl.single-item {
  justify-content: center;
}
</style>
